import {
  agendaSlotTypeSchema,
  periodsOfTheDay,
  pictureSchema,
  UnauthorizedError,
} from '@omecare/utils';
import { UseQueryResult, useQuery } from '@tanstack/react-query';
import { z } from 'zod';
import http from '@/utils/http.service';

const getAccountingWithSlotsSchema = z.object({
  id: z.string(),
  accountPeriod: z.object({
    from: z.coerce.string(),
    to: z.coerce.string(),
  }),
  amountDue: z.coerce.number(),
  amountPaid: z.coerce.number(),
  amountPaidRetrocessionFee: z.coerce.number(),
  amountMedicalAssistantFee: z.coerce.number(),
  amountPayBack: z.coerce.number(),
  medicalAssistantFee: z.coerce.number(),
  createdAt: z.coerce.date(),
  office: z.object({
    id: z.string(),
    name: z.string(),
  }),
  doctorAttending: z.object({
    id: z.string(),
    firstname: z.string(),
    lastname: z.string(),
  }),
  doctorSubstitute: z.object({
    id: z.string(),
    firstname: z.string(),
    lastname: z.string(),
    picture: pictureSchema,
  }),
  agendaSlots: z.array(
    z.object({
      id: z.string(),
      startAt: z.coerce.date(),
      endAt: z.coerce.date(),
      periodOfTheDay: z.enum(periodsOfTheDay),
      type: agendaSlotTypeSchema,
    }),
  ),
});

export type GetAccountingWithSlotsResponse = z.infer<
  typeof getAccountingWithSlotsSchema
>;
export type GetAccountingWithSlotsQuery = {
  id: string;
};

const getAccountingWithSlotsQuery =
  (params: GetAccountingWithSlotsQuery) => async () => {
    return http.accountings
      .getByIdWithSlots(params.id)
      .then<GetAccountingWithSlotsResponse>(({ transform }) => {
        const result = transform.handle({
          attributes: {
            accountPeriod: true,
            amountDue: true,
            amountPaid: true,
            amountPaidRetrocessionFee: true,
            amountMedicalAssistantFee: true,
            amountPayBack: true,
            medicalAssistantFee: true,
            createdAt: true,
          },
          relations: {
            office: {
              name: true,
            },
            doctorAttending: {
              firstname: true,
              lastname: true,
            },
            doctorSubstitute: {
              firstname: true,
              lastname: true,
              picture: true,
            },
            agendaSlots: {
              startAt: true,
              endAt: true,
              periodOfTheDay: true,
              type: true,
            },
          },
          schema: getAccountingWithSlotsSchema,
        });

        return {
          ...result,
          office: result.office!,
          doctorAttending: result.doctorAttending!,
          doctorSubstitute: result.doctorSubstitute!,
          agendaSlots: result.agendaSlots!,
        };
      });
  };

export const useGetAccountingWithSlotsQuery = (
  params: GetAccountingWithSlotsQuery,
  enabled: boolean = true,
): UseQueryResult<GetAccountingWithSlotsResponse, UnauthorizedError> => {
  return useQuery({
    queryFn: getAccountingWithSlotsQuery(params),
    queryKey: ['accouting-with-slots', params.id],
    retry: false,
    enabled,
  });
};
